import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, AssignmentInd, Download } from "@mui/icons-material";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IEvent } from "components/Event";
import {
  Font,
  Image,
  Document,
  Page,
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import {
  getReportEvents as getReportEventsApi,
  addReport as addReportApi,
} from "apis";
import { useSnackbar } from "notistack";

Font.register({ family: "FamilyName", src: "/NotoKufiArabic-Bold.ttf" });

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#FFFFFF",
    direction: "rtl",
  },
  viewer: {
    width: "100%",
    height: "calc(100vh - 140px)",
    border: "none",
  },
});

interface FormFields {
  id: string;
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  address: string;
  additionals: { ethnicity: string; religion: string; languages: string[] };
  classroomId: number;
  parentId: number;
  photo?: string | File;
  fatherFirstName: string;
  motherFirstName: string;
  thirdFirstName: string;
  fatherLastName: string;
  motherLastName: string;
  thirdLastName: string;
  fatherEmail: string;
  motherEmail: string;
  thirdEmail: string;
  fatherMobile: string;
  motherMobile: string;
  thirdMobile: string;
  parent: {
    firstName: string;
    lastName: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      country: string;
      postcode: string;
    };
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  id: number;
  child: FormFields;
}

const PreviewReportDialog: React.FC<Props> = ({ open, onClose, id, child }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [events, setEvents] = useState<IEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setLoading(true);
    getReportEventsApi({ id })
      .then((res) => setEvents(res.data || []))
      .finally(() => setLoading(false));
  }, [id]);

  const MyDoc = (
    <Document>
      <Page size="A4" style={styles.page}>
        <View
          style={{
            color: "white",
            textAlign: "center",
            margin: 30,
            flexDirection: "row",
          }}
        >
          <View>
            <Image
              style={{ width: 60, height: 60, padding: 10 }}
              src="../../logo.jpg"
            />
          </View>
          <View>
            <Text style={{ color: "#000", marginTop: 20 }}>eParentSpace</Text>
          </View>
        </View>
        <View
          style={{
            textAlign: "center",
            fontSize: 40,
            fontFamily: "FamilyName",
          }}
        >
          <Text>{t("posts.learning_story")}</Text>
        </View>
        <View
          style={{ fontSize: 14, fontFamily: "FamilyName", marginLeft: 50 }}
        >
          <View style={{ flexDirection: "row" }}>
            <Text style={{ fontWeight: "bold" }}>{t("labels.name")}: </Text>
            <Text style={{ fontWeight: "bold", direction: "ltr" }}>
              {child.firstName} {child.lastName}
            </Text>
          </View>
          <Text style={{ fontWeight: "bold" }}>
            {t("inputs.birthday")}:{" "}
            {dayjs(child.dateOfBirth).format("DD/MM/YYYY")}
          </Text>
          <Text style={{ fontWeight: "bold" }}>
            {t("inputs.gender")}:{" "}
            {child.gender === "Male" ? t("gender.male") : t("gender.female")}
          </Text>
        </View>
        {events.map((event, index) => (
          <View style={{ marginHorizontal: 30, marginTop: 20 }} key={index}>
            <Text
              style={{ marginHorizontal: 20, fontSize: 14, fontWeight: 900 }}
            >
              [{dayjs(event.createdAt).format("DD/MM/YYYY HH:mm")}]{" "}
              {t("posts." + event.eventType.name.toLowerCase())}
            </Text>
            {Object.keys(event.infos).map((key) => (
              <View style={{ marginTop: 10 }} key={key}>
                <Text
                  style={{
                    marginHorizontal: 20,
                    fontWeight: "ultrabold",
                    fontSize: 14,
                    fontFamily: "FamilyName",
                  }}
                >
                  {t(`${key}`)}:
                </Text>
                <Text
                  style={{
                    marginHorizontal: 20,
                    fontSize: 12,
                    fontWeight: 100,
                  }}
                >
                  {event.infos[key]}
                </Text>
              </View>
            ))}
            <View
              style={{ marginTop: 10, paddingLeft: 10, flexDirection: "row" }}
            >
              {event.images &&
                event.images.length > 0 &&
                event.images.map((image, i) =>
                  image ? (
                    <Image
                      src={image}
                      style={{
                        height: 120,
                        width: 160,
                        padding: 10,
                        paddingRight: 0,
                      }}
                      key={i}
                    />
                  ) : null,
                )}
            </View>
          </View>
        ))}
      </Page>
    </Document>
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const blob = await pdf(MyDoc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `report-${child.firstName}-${child.lastName}-${dayjs().format("YYYYMMDD_HHmmss")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar(t("report.downloaded"), { variant: "success" });

      await addReportApi({ id, blob });
      enqueueSnackbar(t("report.saved"), { variant: "success" });
      onClose();
    } catch {
      enqueueSnackbar(t("report.error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen
      PaperProps={{ sx: { bgcolor: "#f5f5f5" } }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <AssignmentInd sx={{ fontSize: 24 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              {t("report.preview")}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {child.firstName} {child.lastName}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1}>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Download />
              )
            }
            variant="contained"
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.15),
              color: "common.white",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": {
                bgcolor: alpha(theme.palette.common.white, 0.25),
                boxShadow: "none",
              },
            }}
          >
            {saving ? t("labels.pending") : t("buttons.save")}
          </Button>
          <Button
            onClick={onClose}
            startIcon={<Close />}
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.15),
              color: "common.white",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": {
                bgcolor: alpha(theme.palette.common.white, 0.25),
                boxShadow: "none",
              },
            }}
          >
            {t("buttons.cancel")}
          </Button>
        </Box>
      </Box>

      {/* PDF viewer */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            py={8}
          >
            <CircularProgress />
          </Box>
        ) : (
          <PDFViewer style={styles.viewer}>{MyDoc}</PDFViewer>
        )}
      </Box>
    </Dialog>
  );
};

export default PreviewReportDialog;
